import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAddressBook,
  faBuilding,
  faCalendarDays,
  faChartLine,
  faCircleCheck,
  faClipboard,
  faClock,
  faGlobe,
  faHeadset,
  faIdBadge,
  faSitemap,
  faSpinner,
  faTicket,
  faTriangleExclamation,
  faUser,
  faUserShield,
  faUsers,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import RevenueSalesChart from "../components/chart/RevenueSalesChart";
import WeeklyChart from "../components/chart/WeeklyChart";
import useUserMenuItems from "../components/Layout/useUserMenuItems";
import config from "../config";
import { getById, getlist } from "../contexts/apicontexts";

const safeParseJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getRoleNames = (user) => {
  if (!user) return [];

  const fromArray = Array.isArray(user.roleNames)
    ? user.roleNames
    : Array.isArray(user.RoleNames)
      ? user.RoleNames
      : [];

  if (fromArray.length) {
    return fromArray.map((item) => String(item || "").trim()).filter(Boolean);
  }

  const single = user?.roleName ?? user?.role ?? user?.RoleName ?? user?.Role ?? "";
  return single ? [String(single).trim()] : [];
};

const normalizeRole = (rawRoles) => {
  const role = (Array.isArray(rawRoles) ? rawRoles : [rawRoles])
    .map((item) => String(item || "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");

  if (!role) return { key: "user", label: "User" };
  if (role.includes("admin")) return { key: "admin", label: "Admin" };
  if (role.includes("hr") || role.includes("human")) return { key: "hr", label: "HR" };
  if (role.includes("sales") || role.includes("marketing")) return { key: "sales", label: "Sales" };
  if (role.includes("manager") || role.includes("lead")) return { key: "manager", label: "Manager" };
  if (role.includes("support") || role.includes("help")) return { key: "support", label: "Support" };
  if (role.includes("customer")) return { key: "customer", label: "Customer" };
  return { key: "user", label: "User" };
};

const formatDate = (value) => {
  if (!value) return "Not set";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

const getAgreementText = (endDate) => {
  if (!endDate) return "Agreement date not set";

  const end = new Date(endDate);
  const today = new Date();
  const diffMs = end.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "Agreement expired";
  if (diffDays === 0) return "Agreement ends today";
  return `${diffDays} days left on agreement`;
};

const getCurrentUserIdentity = () => {
  const user = safeParseJson(localStorage.getItem("user"));
  return {
    userId: user?.userId ?? user?.UserId ?? user?.id ?? user?.Id ?? "",
    email: String(user?.email ?? user?.Email ?? "").trim(),
    name: String(user?.fullName ?? user?.FullName ?? user?.name ?? user?.userName ?? user?.username ?? "User").trim() || "User",
  };
};

const normalizeStatusCount = (items, status) =>
  (items || []).filter((item) => String(item?.status || "").trim().toLowerCase() === status.toLowerCase()).length;

const buildCustomerDashboardFallback = async () => {
  const identity = getCurrentUserIdentity();

  const [ticketResult, customerResult] = await Promise.allSettled([
    getlist(`${config.baseurl}Ticket/getlist`),
    getlist(`${config.baseurl}Customer/getlist`),
  ]);

  const tickets =
    ticketResult.status === "fulfilled" && ticketResult.value?.status === 200 && Array.isArray(ticketResult.value.data)
      ? ticketResult.value.data
      : [];

  const customers =
    customerResult.status === "fulfilled" && customerResult.value?.status === 200 && Array.isArray(customerResult.value.data)
      ? customerResult.value.data
      : [];

  const matchedCustomer = customers.find((item) => {
    const itemUserId = item?.userId ?? item?.UserId ?? "";
    const itemEmail = String(item?.email ?? item?.Email ?? "").trim().toLowerCase();
    return (identity.userId && itemUserId === identity.userId) || (identity.email && itemEmail === identity.email.toLowerCase());
  });

  const orderedTickets = [...tickets].sort((a, b) => {
    const aDate = new Date(a?.lastReplyAt || a?.createdAt || 0).getTime();
    const bDate = new Date(b?.lastReplyAt || b?.createdAt || 0).getTime();
    return bDate - aDate;
  });

  return {
    customerId: matchedCustomer?.id ?? matchedCustomer?.Id ?? 0,
    name: matchedCustomer?.name ?? matchedCustomer?.Name ?? identity.name,
    customerCode: matchedCustomer?.customerCode ?? matchedCustomer?.CustomerCode ?? "Not available",
    email: matchedCustomer?.email ?? matchedCustomer?.Email ?? identity.email,
    mobile: matchedCustomer?.mobile ?? matchedCustomer?.Mobile ?? "Not added yet",
    whatsapp: matchedCustomer?.whatsApp ?? matchedCustomer?.WhatsApp ?? "",
    domain: matchedCustomer?.domain ?? matchedCustomer?.Domain ?? "Not added yet",
    address: matchedCustomer?.address ?? matchedCustomer?.Address ?? "",
    description: matchedCustomer?.description ?? matchedCustomer?.Description ?? "",
    isCurrentCustomer: matchedCustomer?.isCurrentCustomer ?? matchedCustomer?.IsCurrentCustomer ?? true,
    organizationId: matchedCustomer?.organizationId ?? matchedCustomer?.OrganizationId ?? 0,
    organizationName: matchedCustomer?.organizationName ?? matchedCustomer?.OrganizationName ?? "No organization",
    agreementStartDate: matchedCustomer?.agreementStartDate ?? matchedCustomer?.AgreementStartDate ?? null,
    agreementEndDate: matchedCustomer?.agreementEndDate ?? matchedCustomer?.AgreementEndDate ?? null,
    totalTickets: tickets.length,
    newTickets: normalizeStatusCount(tickets, "New"),
    inProgressTickets: normalizeStatusCount(tickets, "In Progress"),
    onHoldTickets: normalizeStatusCount(tickets, "On Hold"),
    resolvedTickets: normalizeStatusCount(tickets, "Resolved"),
    lastTicketAt: orderedTickets[0]?.lastReplyAt ?? orderedTickets[0]?.createdAt ?? null,
  };
};

const flattenMenuLinks = (groups) => {
  const links = [];
  for (const group of groups || []) {
    for (const item of group?.items || []) {
      if (item?.to) links.push({ to: item.to, label: item.label, icon: item.icon });
      if (item?.isDropdown) {
        for (const sub of item?.subItems || []) {
          if (sub?.to) links.push({ to: sub.to, label: sub.label, icon: sub.icon });
        }
      }
    }
  }

  const seen = new Set();
  return links.filter((x) => {
    const key = String(x?.to || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const Card = ({ children, className = "" }) => (
  <div
    className={[
      "rounded-xl border border-gray-200 bg-white shadow-sm",
      "dark:border-gray-700 dark:bg-gray-800",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

const StatCard = ({ icon, title, value, helper }) => (
  <Card className="p-4 sm:p-5">
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-600/10 text-primary-700 dark:bg-primary-600/20 dark:text-primary-200">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</div>
        <div className="mt-1 truncate text-2xl font-semibold text-gray-900 dark:text-white">{value}</div>
        {helper ? <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helper}</div> : null}
      </div>
    </div>
  </Card>
);

const CustomerDashboard = ({ displayName, quickActions }) => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await getById(`${config.baseurl}Customer/dashboard`);
        if (cancelled) return;

        if (response?.status === 200) {
          setDashboard(response.data || null);
        } else {
          const fallbackData = await buildCustomerDashboardFallback();
          if (!cancelled) setDashboard(fallbackData);
        }
      } catch {
        try {
          const fallbackData = await buildCustomerDashboardFallback();
          if (!cancelled) setDashboard(fallbackData);
        } catch {
          if (!cancelled) setError("Customer dashboard data could not be loaded.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const agreementText = useMemo(() => getAgreementText(dashboard?.agreementEndDate), [dashboard?.agreementEndDate]);
  const openTickets = Number(dashboard?.newTickets || 0) + Number(dashboard?.inProgressTickets || 0) + Number(dashboard?.onHoldTickets || 0);

  if (loading) {
    return (
      <Card className="p-6 text-sm text-gray-600 dark:text-gray-300">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Loading customer dashboard...
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 p-6 text-sm text-red-600 dark:border-red-900/60 dark:text-red-300">
        <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
        {error}
      </Card>
    );
  }

  if (!dashboard) {
    return (
      <Card className="p-6 text-sm text-gray-600 dark:text-gray-300">
        No customer profile is linked with this login yet.
      </Card>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-[linear-gradient(135deg,#0f172a,#1e293b_58%,#334155)] p-5 text-white shadow-sm dark:border-slate-700">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-100">
              Customer Dashboard
            </div>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight">Welcome, {dashboard.name || displayName}</h2>
            <p className="mt-2 text-sm text-slate-200">
              Track your support requests, agreement details, and customer profile from one place.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-200">
              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{dashboard.email || "No email"}</span>
              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{dashboard.organizationName || "No organization"}</span>
              <span className="rounded-full border border-white/15 bg-white/10 px-3 py-1">{agreementText}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/tickets"
              className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-amber-300"
            >
              <FontAwesomeIcon icon={faTicket} />
              Open Tickets
            </Link>
            <Link
              to="/profile"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
            >
              <FontAwesomeIcon icon={faUser} />
              Profile
            </Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon={faTicket} title="Total Tickets" value={dashboard.totalTickets ?? 0} helper="All your support requests" />
        <StatCard icon={faHeadset} title="Open Tickets" value={openTickets} helper="New, in progress, and on hold" />
        <StatCard icon={faCircleCheck} title="Resolved" value={dashboard.resolvedTickets ?? 0} helper="Completed by support" />
        <StatCard icon={faCalendarDays} title="Agreement End" value={formatDate(dashboard.agreementEndDate)} helper={agreementText} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Quick Actions</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Jump into the pages your account can access.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5">
            {quickActions.map((action) => (
              <Link
                key={action.to}
                to={action.to}
                className="group flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 transition hover:border-primary-600/50 hover:bg-white dark:border-gray-700 dark:bg-gray-900/30 dark:hover:border-primary-600/40 dark:hover:bg-gray-900/40"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-700 shadow-sm ring-1 ring-gray-200 group-hover:text-primary-700 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700">
                    <FontAwesomeIcon icon={action.icon || faClipboard} />
                  </span>
                  <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">{action.label}</span>
                </div>
                <span className="text-xs font-medium text-gray-500 group-hover:text-primary-700 dark:text-gray-400 dark:group-hover:text-primary-200">Open</span>
              </Link>
            ))}
          </div>
        </Card>

        <Card>
          <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Today's Focus</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Keep these items in view.</p>
          </div>
          <div className="space-y-3 p-4 sm:p-5">
            {[
              { label: "Check ticket replies from support", icon: faHeadset },
              { label: "Review agreement expiry date", icon: faCalendarDays },
              { label: "Keep your contact information updated", icon: faUser },
              { label: "Raise a new ticket for urgent issues", icon: faClipboard },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-md bg-primary-600/10 text-primary-700 dark:bg-primary-600/20 dark:text-primary-200">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{item.label}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Customer Profile</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Primary information from your customer record.</p>
          </div>
          <div className="space-y-3 p-4 sm:p-5">
            {[
              { label: "Customer Code", value: dashboard.customerCode || "Not available", icon: faAddressBook },
              { label: "Organization", value: dashboard.organizationName || "Not linked yet", icon: faBuilding },
              { label: "Domain", value: dashboard.domain || "Not added yet", icon: faGlobe },
              { label: "Mobile", value: dashboard.mobile || "Not added yet", icon: faUser },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3 rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-900/30">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-700 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700">
                  <FontAwesomeIcon icon={item.icon} />
                </span>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{item.label}</div>
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{item.value}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Support Summary</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Account and support timeline at a glance.</p>
          </div>
          <div className="space-y-3 p-4 sm:p-5">
            {[
              { label: "Agreement Start", value: formatDate(dashboard.agreementStartDate) },
              { label: "Agreement End", value: formatDate(dashboard.agreementEndDate) },
              { label: "Last Ticket Activity", value: formatDate(dashboard.lastTicketAt) },
              { label: "Status", value: dashboard.isCurrentCustomer ? "Active Customer" : "Inactive Customer" },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3 dark:border-gray-700">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{item.label}</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { menuItems, loading: menuLoading } = useUserMenuItems();

  const user = useMemo(() => safeParseJson(localStorage.getItem("user")), []);
  const roleInfo = useMemo(() => normalizeRole(getRoleNames(user)), [user]);

  const displayName = useMemo(() => {
    const name =
      user?.fullName ?? user?.FullName ?? user?.name ?? user?.userName ?? user?.username ?? user?.email ?? user?.Email ?? "User";
    return String(name).trim() || "User";
  }, [user]);

  const today = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(undefined, { weekday: "short", year: "numeric", month: "short", day: "2-digit" }).format(
        new Date()
      );
    } catch {
      return new Date().toDateString();
    }
  }, []);

  const quickActions = useMemo(() => {
    const all = flattenMenuLinks(menuItems).filter((x) => x.to !== "/dashboard");
    const preferred = ["/my-tasks", "/all-tasks", "/client/customer", "/client/organization", "/hr/employee", "/user/alluser", "/user/roles", "/kanban"];
    const supportAction = { to: "/tickets", label: "Support Tickets", icon: faClipboard };
    const profileAction = { to: "/profile", label: "Profile", icon: faAddressBook };

    const byTo = new Map(all.map((x) => [x.to, x]));
    const picked = preferred.map((to) => byTo.get(to)).filter(Boolean);
    const rest = all.filter((x) => !preferred.includes(x.to) && x.to !== "/tickets");
    const ordered = roleInfo.key === "customer"
      ? [supportAction, byTo.get("/client/customer"), profileAction, ...picked, ...rest]
      : [supportAction, profileAction, ...picked, ...rest];

    const seen = new Set();
    return ordered.filter((item) => {
      const key = String(item?.to || "").trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    }).slice(0, 6);
  }, [menuItems, roleInfo.key]);

  const showAnalytics = roleInfo.key === "admin" || roleInfo.key === "manager" || roleInfo.key === "sales";

  const stats = useMemo(() => {
    // Placeholder metrics (hook up API counts later if/when endpoints are available).
    if (roleInfo.key === "admin") {
      return [
        { icon: faUsers, title: "Users", value: "—", helper: "System accounts" },
        { icon: faUserShield, title: "Permissions", value: "—", helper: "Menus & roles" },
        { icon: faSitemap, title: "Organizations", value: "—", helper: "Client companies" },
        { icon: faBuilding, title: "Employees", value: "—", helper: "Active employees" },
      ];
    }
    if (roleInfo.key === "hr") {
      return [
        { icon: faBuilding, title: "Employees", value: "—", helper: "Active employees" },
        { icon: faSitemap, title: "Departments", value: "—", helper: "Org structure" },
        { icon: faIdBadge, title: "Designations", value: "—", helper: "Roles & titles" },
        { icon: faClock, title: "Pending", value: "—", helper: "Onboarding items" },
      ];
    }
    if (roleInfo.key === "sales") {
      return [
        { icon: faSitemap, title: "Organizations", value: "—", helper: "Accounts" },
        { icon: faUsers, title: "Customers", value: "—", helper: "Contacts" },
        { icon: faChartLine, title: "Pipeline", value: "—", helper: "This month" },
        { icon: faCircleCheck, title: "Follow-ups", value: "—", helper: "Due today" },
      ];
    }
    return [
      { icon: faUsers, title: "Customers", value: "—", helper: "Assigned to you" },
      { icon: faSitemap, title: "Organizations", value: "—", helper: "Active accounts" },
      { icon: faCircleCheck, title: "Tasks", value: "—", helper: "Due today" },
      { icon: faClock, title: "Reminders", value: "—", helper: "Next 7 days" },
    ];
  }, [roleInfo.key]);

  const focusItems = useMemo(() => {
    if (roleInfo.key === "admin") {
      return [
        { label: "Review new users & roles", icon: faUserShield },
        { label: "Audit menu permissions", icon: faCircleCheck },
        { label: "Check system activity", icon: faClock },
      ];
    }
    if (roleInfo.key === "hr") {
      return [
        { label: "Complete pending onboarding", icon: faCircleCheck },
        { label: "Update employee profiles", icon: faIdBadge },
        { label: "Verify department/designation lists", icon: faSitemap },
      ];
    }
    if (roleInfo.key === "sales") {
      return [
        { label: "Follow up on priority customers", icon: faUsers },
        { label: "Update pipeline in Kanban", icon: faChartLine },
        { label: "Confirm organization details", icon: faSitemap },
      ];
    }
    return [
      { label: "Review your quick actions", icon: faWandMagicSparkles },
      { label: "Open a support ticket if you need help", icon: faClipboard },
      { label: "Update your profile information", icon: faIdBadge },
      { label: "Plan today's tasks", icon: faClock },
    ];
  }, [roleInfo.key]);

  if (roleInfo.key === "customer") {
    return <CustomerDashboard displayName={displayName} quickActions={quickActions} />;
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Welcome back, <span className="font-medium text-gray-900 dark:text-white">{displayName}</span>.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-primary-600/10 px-3 py-1 text-xs font-semibold text-primary-700 dark:bg-primary-600/20 dark:text-primary-200">
            {roleInfo.label}
          </span>
          <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
            {today}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((s) => (
          <StatCard key={s.title} icon={s.icon} title={s.title} value={s.value} helper={s.helper} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="flex flex-col gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Quick actions</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Shortcuts based on your role & permissions.
              </p>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{menuLoading ? "Loading menus…" : "Ready"}</div>
          </div>

          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 sm:p-5">
            {quickActions.length ? (
              quickActions.map((a) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className={[
                    "group flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 transition",
                    "hover:border-primary-600/50 hover:bg-white",
                    "dark:border-gray-700 dark:bg-gray-900/30 dark:hover:border-primary-600/40 dark:hover:bg-gray-900/40",
                  ].join(" ")}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-700 shadow-sm ring-1 ring-gray-200 group-hover:text-primary-700 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-700">
                      <FontAwesomeIcon icon={a.icon || faWandMagicSparkles} />
                    </span>
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">{a.label}</span>
                  </div>
                  <span className="text-xs font-medium text-gray-500 group-hover:text-primary-700 dark:text-gray-400 dark:group-hover:text-primary-200">
                    Open
                  </span>
                </Link>
              ))
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-300">
                No quick actions found for your account.
              </div>
            )}
          </div>
        </Card>

        <Card>
          <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Today’s focus</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">A small checklist to stay on track.</p>
          </div>
          <div className="space-y-3 p-4 sm:p-5">
            {focusItems.map((x) => (
              <div key={x.label} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-md bg-primary-600/10 text-primary-700 dark:bg-primary-600/20 dark:text-primary-200">
                  <FontAwesomeIcon icon={x.icon} />
                </span>
                <div className="text-sm font-medium text-gray-900 dark:text-white">{x.label}</div>
              </div>
            ))}

            <div className="pt-2">
              <Link
                to="/profile"
                className="inline-flex w-full items-center justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              >
                Go to profile
              </Link>
            </div>
          </div>
        </Card>
      </div>

      {showAnalytics ? (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <Card className="xl:col-span-8">
            <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Revenue & sales</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Overview for the last 6 months.</p>
            </div>
            <div className="p-4 sm:p-5">
              <RevenueSalesChart />
            </div>
          </Card>

          <Card className="xl:col-span-4">
            <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Weekly performance</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Trend snapshot.</p>
            </div>
            <div className="p-4 sm:p-5">
              <WeeklyChart />
            </div>
          </Card>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Activity</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Recent updates (demo).</p>
          </div>
          <div className="space-y-3 p-4 sm:p-5">
            {[
              { t: "Customer profile updated", d: "Just now" },
              { t: "Organization added", d: "Today" },
              { t: "Employee record edited", d: "Yesterday" },
            ].map((a) => (
              <div key={a.t} className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-4 py-3 dark:bg-gray-900/30">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">{a.t}</div>
                  <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{a.d}</div>
                </div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">•</span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div className="border-b border-gray-200 p-4 dark:border-gray-700 sm:p-5">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Modules</h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">What you can access right now.</p>
          </div>
          <div className="flex flex-wrap gap-2 p-4 sm:p-5">
            {(menuItems || []).length ? (
              menuItems.map((g) => (
                <span
                  key={g.group}
                  className="inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-semibold text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                >
                  {g.group}
                </span>
              ))
            ) : (
              <span className="text-sm text-gray-600 dark:text-gray-300">No modules found.</span>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;

